import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  UserGroupIcon,
  ClipboardDocumentListIcon,
  CurrencyDollarIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  TicketIcon,
  UsersIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';
import { Card, CardHeader, Badge, Spinner, Avatar } from '../components/ui';
import { ConversionChart } from '../components/charts/ConversionChart';
import { TransactionChart } from '../components/charts/TransactionChart';
import { useAuthStore } from '../stores';
import { dashboardService } from '../services/dashboard.service';
import OnboardingStepper from '../components/onboarding/OnboardingStepper';
import type { DashboardStats, RecentOrder, ConversionTrendData, TransactionTrendData } from '../types';
import { formatCurrency, formatRelativeTime, cn } from '../utils/helpers';
import { getStatusDisplayName, getStatusBadgeVariant } from '../utils/orderStatus';

interface StatCardProps {
  title: string;
  value: string | number;
  change?: number;
  icon: React.ReactNode;
  iconBg: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, change, icon, iconBg }) => {
  const isPositive = change !== undefined && change >= 0;

  return (
    <Card className="relative overflow-hidden">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-secondary-500 dark:text-secondary-400">{title}</p>
          <p className="mt-2 text-2xl font-bold text-secondary-900 dark:text-secondary-50">{value}</p>
          {change !== undefined && (
            <div className="mt-2 flex items-center">
              {isPositive ? (
                <ArrowTrendingUpIcon className="h-4 w-4 text-success-500" />
              ) : (
                <ArrowTrendingDownIcon className="h-4 w-4 text-danger-500" />
              )}
              <span
                className={cn(
                  'ml-1 text-sm font-medium',
                  isPositive ? 'text-success-600 dark:text-success-400' : 'text-danger-600 dark:text-danger-400'
                )}
              >
                {Math.abs(change)}%
              </span>
              <span className="ml-1 text-sm text-secondary-500 dark:text-secondary-400">vs last month</span>
            </div>
          )}
        </div>
        <div className={cn('p-3 rounded-xl', iconBg)}>{icon}</div>
      </div>
    </Card>
  );
};

// Using shared getStatusBadgeVariant from utils/orderStatus.ts

export const Dashboard: React.FC = () => {
  const { user, business } = useAuthStore();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentOrders, setRecentOrders] = useState<RecentOrder[]>([]);
  const [conversionTrend, setConversionTrend] = useState<ConversionTrendData | null>(null);
  const [transactionTrend, setTransactionTrend] = useState<TransactionTrendData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Check for onboarding flag on initial load
  useEffect(() => {
    const shouldShowOnboarding = localStorage.getItem('show_onboarding') === 'true';
    if (shouldShowOnboarding) {
      setShowOnboarding(true);
    }
  }, []);

  const handleCloseOnboarding = () => {
    setShowOnboarding(false);
    localStorage.removeItem('show_onboarding');
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [statsData, ordersData, trendData, txTrendData] = await Promise.all([
          dashboardService.getStats(),
          dashboardService.getRecentOrders(5),
          dashboardService.getConversionTrend(15),
          dashboardService.getTransactionTrend(15),
        ]);
        setStats(statsData);
        setRecentOrders(ordersData);
        setConversionTrend(trendData);
        setTransactionTrend(txTrendData);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
        // Mock data for development
        setStats({
          totalPatients: 1247,
          totalOrders: 3562,
          pendingOrders: 23,
          totalRevenue: 156780,
          patientsChange: 12,
          ordersChange: 8,
          revenueChange: 15,
        });
        setRecentOrders([
          { id: '1', patientName: 'John Smith', medication: 'Semaglutide 0.5mg', status: 'processing', amount: 299, date: new Date().toISOString() },
          { id: '2', patientName: 'Sarah Johnson', medication: 'Tirzepatide 2.5mg', status: 'shipped', amount: 450, date: new Date(Date.now() - 86400000).toISOString() },
          { id: '3', patientName: 'Mike Davis', medication: 'Semaglutide 1mg', status: 'pending', amount: 349, date: new Date(Date.now() - 172800000).toISOString() },
          { id: '4', patientName: 'Emily Brown', medication: 'Tirzepatide 5mg', status: 'delivered', amount: 550, date: new Date(Date.now() - 259200000).toISOString() },
          { id: '5', patientName: 'David Wilson', medication: 'Semaglutide 2.4mg', status: 'processing', amount: 499, date: new Date(Date.now() - 345600000).toISOString() },
        ]);
        setConversionTrend({ intakeSessions: [], orders: [] });
        setTransactionTrend({ transactions: [] });
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Onboarding Stepper Modal */}
      <OnboardingStepper isOpen={showOnboarding} onClose={handleCloseOnboarding} />

      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-primary-500 to-primary-600 rounded-2xl p-6 text-white">
        <h2 className="text-2xl font-display font-semibold">
          Welcome back, {user?.firstName}!
        </h2>
        <p className="mt-1 text-primary-100">
          Here's what's happening with {business?.name || 'your business'} today.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Patients"
          value={stats?.totalPatients.toLocaleString() || '0'}
          change={stats?.patientsChange}
          icon={<UserGroupIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />}
          iconBg="bg-primary-50 dark:bg-primary-900/30"
        />
        <StatCard
          title="Total Orders"
          value={stats?.totalOrders.toLocaleString() || '0'}
          change={stats?.ordersChange}
          icon={<ClipboardDocumentListIcon className="h-6 w-6 text-accent-600 dark:text-accent-400" />}
          iconBg="bg-accent-50 dark:bg-accent-900/30"
        />
        <StatCard
          title="Pending Orders"
          value={stats?.pendingOrders || '0'}
          icon={<ClockIcon className="h-6 w-6 text-warning-600 dark:text-warning-400" />}
          iconBg="bg-warning-50 dark:bg-warning-900/30"
        />
        <StatCard
          title="Total Revenue"
          value={formatCurrency(stats?.totalRevenue || 0)}
          change={stats?.revenueChange}
          icon={<CurrencyDollarIcon className="h-6 w-6 text-success-600 dark:text-success-400" />}
          iconBg="bg-success-50 dark:bg-success-900/30"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {conversionTrend && (
          <Card>
            <CardHeader
              title="Intake to Order Conversion"
              subtitle="Daily intake sessions vs completed orders — last 15 days"
            />
            <ConversionChart data={conversionTrend} days={15} />
          </Card>
        )}
        {transactionTrend && (
          <Card>
            <CardHeader
              title="Daily Transactions"
              subtitle="Subscription payment captures — last 15 days"
            />
            <TransactionChart data={transactionTrend} days={15} />
          </Card>
        )}
      </div>

      {/* Recent Orders and Quick Links */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Orders */}
        <Card className="lg:col-span-2">
          <CardHeader
            title="Recent Orders"
            subtitle="Latest orders from your patients"
            action={
              <Link
                to="/orders"
                className="text-sm font-medium text-primary-500 hover:text-primary-600"
              >
                View all
              </Link>
            }
          />
          <div className="space-y-4">
            {recentOrders.map((order) => (
              <div
                key={order.id}
                className="flex items-center justify-between p-4 rounded-lg bg-secondary-50 dark:bg-secondary-700/50 hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors"
              >
                <div className="flex items-center space-x-4">
                  <Avatar
                    firstName={order.patientName.split(' ')[0]}
                    lastName={order.patientName.split(' ')[1] || ''}
                    size="md"
                  />
                  <div>
                    <p className="font-medium text-secondary-900 dark:text-secondary-50">{order.patientName}</p>
                    <p className="text-sm text-secondary-500 dark:text-secondary-400">{order.medication}</p>
                  </div>
                </div>
                <div className="text-right">
                  <Badge variant={getStatusBadgeVariant(order.status)} dot>
                    {getStatusDisplayName(order.status)}
                  </Badge>
                  <p className="mt-1 text-sm font-medium text-secondary-900 dark:text-secondary-50">
                    {formatCurrency(order.amount)}
                  </p>
                  <p className="text-xs text-secondary-500 dark:text-secondary-400">{formatRelativeTime(order.date)}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Quick Links */}
        <Card>
          <CardHeader title="Quick Actions" subtitle="Common tasks" />
          <div className="space-y-3">
            {/* Create Order button - temporarily disabled, may be re-enabled for specific clients later */}
            {/* <Link
              to="/orders/new"
              className="flex items-center p-3 rounded-lg bg-primary-50 dark:bg-primary-900/30 hover:bg-primary-100 dark:hover:bg-primary-900/50 transition-colors group"
            >
              <div className="w-10 h-10 rounded-lg bg-primary-500 flex items-center justify-center">
                <ClipboardDocumentListIcon className="h-5 w-5 text-white" />
              </div>
              <div className="ml-3">
                <p className="font-medium text-secondary-900 dark:text-secondary-50 group-hover:text-primary-700 dark:group-hover:text-primary-400">
                  New Order
                </p>
                <p className="text-sm text-secondary-500 dark:text-secondary-400">Create a new patient order</p>
              </div>
            </Link> */}

            <Link
              to="/patients"
              className="flex items-center p-3 rounded-lg bg-accent-50 dark:bg-accent-900/30 hover:bg-accent-100 dark:hover:bg-accent-900/50 transition-colors group"
            >
              <div className="w-10 h-10 rounded-lg bg-accent-500 flex items-center justify-center">
                <UserGroupIcon className="h-5 w-5 text-white" />
              </div>
              <div className="ml-3">
                <p className="font-medium text-secondary-900 dark:text-secondary-50 group-hover:text-accent-700 dark:group-hover:text-accent-400">
                  View Patients
                </p>
                <p className="text-sm text-secondary-500 dark:text-secondary-400">Browse your patient list</p>
              </div>
            </Link>

            <Link
              to="/medications"
              className="flex items-center p-3 rounded-lg bg-success-50 dark:bg-success-900/30 hover:bg-success-100 dark:hover:bg-success-900/50 transition-colors group"
            >
              <div className="w-10 h-10 rounded-lg bg-success-500 flex items-center justify-center">
                <CurrencyDollarIcon className="h-5 w-5 text-white" />
              </div>
              <div className="ml-3">
                <p className="font-medium text-secondary-900 dark:text-secondary-50 group-hover:text-success-700 dark:group-hover:text-success-400">
                  Medications
                </p>
                <p className="text-sm text-secondary-500 dark:text-secondary-400">Manage your medication catalog</p>
              </div>
            </Link>

            <Link
              to="/coupons"
              className="flex items-center p-3 rounded-lg bg-warning-50 dark:bg-warning-900/30 hover:bg-warning-100 dark:hover:bg-warning-900/50 transition-colors group"
            >
              <div className="w-10 h-10 rounded-lg bg-warning-500 flex items-center justify-center">
                <TicketIcon className="h-5 w-5 text-white" />
              </div>
              <div className="ml-3">
                <p className="font-medium text-secondary-900 dark:text-secondary-50 group-hover:text-warning-700 dark:group-hover:text-warning-400">
                  Create a Coupon
                </p>
                <p className="text-sm text-secondary-500 dark:text-secondary-400">Set up discounts and promotions</p>
              </div>
            </Link>

            <Link
              to="/users"
              className="flex items-center p-3 rounded-lg bg-primary-50 dark:bg-primary-900/30 hover:bg-primary-100 dark:hover:bg-primary-900/50 transition-colors group"
            >
              <div className="w-10 h-10 rounded-lg bg-primary-500 flex items-center justify-center">
                <UsersIcon className="h-5 w-5 text-white" />
              </div>
              <div className="ml-3">
                <p className="font-medium text-secondary-900 dark:text-secondary-50 group-hover:text-primary-700 dark:group-hover:text-primary-400">
                  Manage Users
                </p>
                <p className="text-sm text-secondary-500 dark:text-secondary-400">Add or edit team members</p>
              </div>
            </Link>

            <Link
              to="/email-templates"
              className="flex items-center p-3 rounded-lg bg-danger-50 dark:bg-danger-900/30 hover:bg-danger-100 dark:hover:bg-danger-900/50 transition-colors group"
            >
              <div className="w-10 h-10 rounded-lg bg-danger-500 flex items-center justify-center">
                <EnvelopeIcon className="h-5 w-5 text-white" />
              </div>
              <div className="ml-3">
                <p className="font-medium text-secondary-900 dark:text-secondary-50 group-hover:text-danger-700 dark:group-hover:text-danger-400">
                  Configure Emails
                </p>
                <p className="text-sm text-secondary-500 dark:text-secondary-400">Customize email templates</p>
              </div>
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
